import classNames from 'classnames';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight } from 'react-icons/fa6';

import { PageContainer } from '../../../components/layout/page-container';
import { PageContentAnimation } from '../../../components/layout/page-content-animation';
import { PortfolioSidebar } from './portfolio-sidebar.component';

/**
 * Two-column frame for the economy pages, built to the same metrics as `ReportPageLayout`:
 * a collapsible 320px rail beside the content, both sitting on the app background rather
 * than on a surface of their own.
 */
export function EconomyLayout({ children }: PropsWithChildren) {
  const { t } = useTranslation('economy');
  const [isOpen, setIsOpen] = useState(true);

  return (
    <PageContentAnimation>
      <PageContainer>
        <div className={classNames('grid gap-5', isOpen ? 'lg:grid-cols-[320px_1fr]' : 'lg:grid-cols-[0_1fr]')}>
          <aside className="flex flex-col gap-5 overflow-y-auto scroll-smooth">
            <PortfolioSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              aria-label={t('sidebar.expand')}
              /*
               * `fixed`, not `absolute`: the nearest positioned ancestor is the wrapper
               * around the whole page, so `top-1/2` there is half the page's height —
               * on a long page the button sits somewhere in the middle of the scroll and
               * is not on screen when you need it. Anchored to the viewport instead.
               */
              className={classNames(
                'fixed left-0 top-1/2 z-10 -translate-y-1/2 rounded-r-lg bg-surface-elevated p-2 transition-colors hover:bg-brand-primary-hover',
                isOpen ? 'pointer-events-none opacity-0' : 'opacity-100',
              )}
            >
              <FaChevronRight className="h-4 w-4 text-white" />
            </button>
          </aside>
          <div className="flex flex-col gap-5">{children}</div>
        </div>
      </PageContainer>
    </PageContentAnimation>
  );
}

export default EconomyLayout;
